import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

import { RoleName, UserStatus } from 'chaika-enums';
import type { UserData } from 'chaika-frontend-types/frontend-cms';

import { AnyOptionMasculine, OptionType } from '@components/Select';
import { makeUserLabel } from '@user/utils/makeUserLabel';

import { userQueryOptions, type UserListFilters } from '@user/queries/userQueries';
import { specialtyQueryOptions } from '@services/queries/specialtyQueries';
import { queryClient } from '@store/queryClient';

export interface UseUsersOptionsParams {
    onlyDoctors?: boolean;
    onlyEpisodeManagers?: boolean;
    withoutTechnical?: boolean;
    /** Server-side filter: only users with ALL of the listed roles. */
    roleNames?: RoleName[];
    /** When true, archived users are included in default/search results too. */
    includeArchived?: boolean;
    /**
     * Label format. `withFullName` wins over `withInitials`; if neither is set,
     * the label falls back to `${lastName} ${firstName}`.
     */
    withFullName?: boolean;
    withInitials?: boolean;
    withSpecialty?: boolean;
    withAnyOption?: boolean;
    firstUserId?: string;
    ids?: string[];
    practiceOrganizationId?: string[];
    practiceRole?: UserListFilters['practiceRole'];
    limit?: number;
    selectedUserIds: string[];
}

interface Data {
    defaultOptions: OptionType[];
    selectedOptions: OptionType[];
    loadOptions(name: string): Promise<OptionType[]>;
}

export function useUsersOptions({
    onlyDoctors,
    onlyEpisodeManagers,
    withoutTechnical,
    roleNames,
    includeArchived,
    withFullName,
    withInitials,
    withSpecialty,
    withAnyOption,
    firstUserId,
    ids,
    practiceOrganizationId,
    practiceRole,
    limit,
    selectedUserIds,
}: UseUsersOptionsParams): Data {
    const filters: Omit<UserListFilters, 'name'> = {
        statuses: includeArchived ? undefined : UserStatus.Active,
        isDoctor: onlyDoctors || undefined,
        canBeEpisodeManager: onlyEpisodeManagers || undefined,
        withoutTechnical: withoutTechnical || undefined,
        roleNames: roleNames && roleNames.length > 0 ? roleNames : undefined,
        ids: ids && ids.length > 0 ? ids : undefined,
        practiceOrganizationId,
        practiceRole,
        limit,
    };

    const { data: defaultUsers = [] } = useQuery(userQueryOptions.list(filters));

    const firstUserIds = firstUserId && (!filters.ids || filters.ids.includes(firstUserId)) ? [firstUserId] : [];
    const { data: firstUserResult = [] } = useQuery({
        ...userQueryOptions.list({ ...filters, ids: firstUserIds }),
        enabled: firstUserIds.length > 0,
    });

    const { data: specialties = [] } = useQuery({
        ...specialtyQueryOptions.list(),
        enabled: !!withSpecialty,
    });

    const { data: selectedUsers = [] } = useQuery({
        ...userQueryOptions.list({ ids: selectedUserIds }),
        enabled: selectedUserIds.length > 0,
    });

    function makeLabel(user: UserData): string {
        const base = makeUserLabel(user, { withFullName, withInitials });

        if (withSpecialty && user.mainSpecialtyId) {
            const specialty = specialties.find(s => s.id === user.mainSpecialtyId);
            if (specialty) {
                return `${base} - (${specialty.name})`;
            }
        }
        return base;
    }

    function processUsers(users: UserData[]): OptionType[] {
        const merged = new Map<string, OptionType>();
        users.forEach(user => {
            merged.set(user.id, { value: user.id, label: makeLabel(user) });
        });
        const allOptions = [...merged.values()];
        const firstUserOption = allOptions.find(({ value }) => value === firstUserId);

        const options = firstUserOption
            ? [firstUserOption, ...allOptions.filter(option => option !== firstUserOption)]
            : allOptions;

        return withAnyOption ? [AnyOptionMasculine, ...options] : options;
    }

    const debouncedFetch = useMemo(
        () => AwesomeDebouncePromise((q: UserListFilters) => queryClient.fetchQuery(userQueryOptions.list(q)), 300),
        [],
    );

    async function loadOptions(name: string): Promise<OptionType[]> {
        const trimmed = name.trim();
        const users = await debouncedFetch({ ...filters, name: trimmed || undefined });
        return processUsers(users);
    }

    return {
        defaultOptions: processUsers([...firstUserResult, ...defaultUsers]),
        selectedOptions: selectedUsers.map(user => ({
            value: user.id,
            label: makeLabel(user),
        })),
        loadOptions,
    };
}
